import { HandoverContractPdf } from './handoverQueries';

function parsePdfPayload(raw) {
  let value = raw;

  for (let i = 0; i < 3; i += 1) {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }

  return typeof value === 'object' && value != null ? value : null;
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function fetchContractPdfPayload(client, handoverId) {
  const res = await client.graphql({
    query: HandoverContractPdf,
    variables: { handoverId },
    authMode: 'userPool',
  });

  if (Array.isArray(res?.errors) && res.errors.length > 0) {
    throw new Error(res.errors[0]?.message || 'GraphQL error while fetching contract PDF');
  }

  const raw = res?.data?.handoverContractPdf;
  if (raw == null) {
    throw new Error(
      'Server returned an empty PDF response. Confirm you are logged in as admin or country admin, then run amplify push again.'
    );
  }

  const parsed = parsePdfPayload(raw);
  if (!parsed) {
    throw new Error('Server returned an unreadable PDF response.');
  }

  return parsed;
}

function triggerBrowserDownload({ downloadUrl, filename, pdfBase64, contentType }) {
  if (downloadUrl) {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'handover-contract.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  if (!pdfBase64) {
    throw new Error('No PDF returned from server');
  }

  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const blob = new Blob([bytes], {
    type: contentType || 'application/pdf',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'handover-contract.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadHandoverContractPdf(
  client,
  handoverId,
  { onPending } = {}
) {
  if (!handoverId) throw new Error('Missing handover id');

  const maxAttempts = 30;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const data = await fetchContractPdfPayload(client, handoverId);

    if (data?.status === 'ready' && (data.downloadUrl || data.pdfBase64)) {
      triggerBrowserDownload(data);
      return;
    }

    if (data?.status === 'pending') {
      onPending?.(data.message, attempt);
      await sleep(3000);
      continue;
    }

    if (data?.downloadUrl || data.pdfBase64) {
      triggerBrowserDownload(data);
      return;
    }

    throw new Error(data?.message || 'Unexpected PDF response from server');
  }

  throw new Error(
    'PDF generation is taking longer than expected. Please try again in a minute.'
  );
}
